import React, { useState, useEffect } from "react";
import { Modal, Select, Input, Form, Row, Col, Table, Button } from "antd";
import { Button as ButtonDto, ButtonFunction, ButtonMetadata, Metadata } from "../../../services/ApiTypes.ts";
import { ButtonItem } from "./ButtonItem.ts";
import { DeleteOutlined } from "@ant-design/icons";
import "./ButtonEditModal.css";

const { Option } = Select;
const emptyGuid = "00000000-0000-0000-0000-000000000000"

interface ButtonEditModalProps {
  isVisible: boolean;
  buttonItem: ButtonItem;
  onClose: () => void;
  onSave: (updatedButton: ButtonItem) => void;
}

const ButtonEditModal: React.FC<ButtonEditModalProps> = ({ isVisible, buttonItem, onClose, onSave }) => {
  const [buttonId, setButtonId] = useState<number>(buttonItem.id);
  const [editedButton, setEditedButton] = useState<ButtonDto>(buttonItem.button);
  const [metadataRows, setMetadataRows] = useState<ButtonMetadata[]>([]);
  const [isOkButtonEnabled, setIsOkButtonEnabled] = useState<boolean>(false);
  const [isSubmitButton, setIsSubmitButton] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    setButtonId(buttonItem.id);
    setEditedButton(buttonItem.button);
    setMetadataRows(buttonItem.button.buttonMetadata);
    setIsSubmitButton(buttonItem.button.function === ButtonFunction.Submit);
  }, [buttonItem]);

  useEffect(() => {
    setIsDisabled(editedButton.function === ButtonFunction.Reset);
  }, [editedButton.function]);

  useEffect(() => {
    const allMetadataFieldsSet = metadataRows.every((row) => row.fieldName.trim() !== "");
    const isButtonTextSet = editedButton.text.trim() !== "";
    const isNonResetFieldsSet =
      editedButton.function === ButtonFunction.Reset ||
      (editedButton.workflowService.trim() !== "" && editedButton.workflowPlanId !== null);

    setIsOkButtonEnabled(allMetadataFieldsSet && isButtonTextSet && isNonResetFieldsSet);
  }, [metadataRows, editedButton]);

  const handleSaveChanges = () => {
    onSave({ button: {...editedButton, buttonMetadata: metadataRows}, id: buttonId });
    onClose();
  };

  const handleInputChange = (field: keyof ButtonDto, value: any) => {
    setEditedButton({ ...editedButton, [field]: value });
  };

  const handleMetadataChange = (index: number, field: keyof ButtonMetadata, value: any) => {
    let updatedRows = [...metadataRows];
    updatedRows[index] = { ...updatedRows[index] };
    updatedRows[index][field] = value;
    setMetadataRows(updatedRows);
  };

  const addMetadataRow = () => {
    setMetadataRows([...metadataRows, { id: emptyGuid, fieldName: "", metadata: Metadata.Design }]);
  };

  const removeMetadataRow = (index: number) => {
    const updatedRows = metadataRows.filter((_, i) => i !== index);
    setMetadataRows(updatedRows);
  };

  const metadataColumns = [
    {
      title: "Metadata Field",
      dataIndex: "metadata",
      key: "metadata",
      render: (_: any, record: ButtonMetadata, index: number) => (
        <Select
          value={record.metadata}
          className="metadata-field-column"
          onChange={(value) => handleMetadataChange(index, "metadata", value)}
          disabled={isDisabled}
        >
          {Object.values(Metadata).map((metadata) => (
            <Option key={metadata} value={metadata}>
              {metadata}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Field Name",
      dataIndex: "fieldName",
      key: "fieldName",
      render: (_: any, record: ButtonMetadata, index: number) => (
        <Input
          className="field-name-column"
          value={record.fieldName}
          onChange={(e) => handleMetadataChange(index, "fieldName", e.target.value)}
          disabled={isDisabled}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, __: ButtonMetadata, index: number) => (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={() => removeMetadataRow(index)}
          disabled={isDisabled}
          data-testid="deleteButton"
        />
      ),
    },
  ];

  return (
    <Modal
      title="Edit Button"
      className="custom-modal"
      open={isVisible}
      onCancel={onClose}
      onOk={handleSaveChanges}
      okText="Ok"
      okButtonProps={{ disabled: !isOkButtonEnabled }}
      width={"700px"}
    >
      {editedButton && (
        <Form layout="vertical" className="button-edit-form">
          <Row>
            <Col span={8}>
              <label htmlFor="text">Name<span className="required-indicator"> *</span></label>
            </Col>
            <Col span={6}>
              <Form.Item>
                <Input
                  value={editedButton.text}
                  onChange={(e) => handleInputChange("text", e.target.value)}
                  data-testid="textInput"
                />
              </Form.Item>
            </Col>
          </Row>
          {!isSubmitButton && (
            <Row>
              <Col span={8}>
                <label htmlFor="function">Button Type</label>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Select
                    value={editedButton.function}
                    onChange={(value) => {
                      handleInputChange("function", value as ButtonFunction);
                    }}
                    data-testid="buttonTypeInput"
                  >
                    <Option value={ButtonFunction.Other}>Workflow</Option>
                    <Option value={ButtonFunction.Reset}>Reset</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row>
            <Col span={8}>
              <label htmlFor="workflowService">
                Workflow Service{editedButton.function !== ButtonFunction.Reset && <span className="required-indicator"> *</span>}
              </label>
            </Col>
            <Col span={6}>
              <Form.Item>
                <Input
                  value={editedButton.workflowService}
                  onChange={(e) => handleInputChange("workflowService", e.target.value)}
                  disabled={isDisabled}
                  data-testid="workflowServiceInput"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={8}>
              <label htmlFor="workflowPlanId">
                Workflow Plan Id{editedButton.function !== ButtonFunction.Reset && <span className="required-indicator"> *</span>}
              </label>
            </Col>
            <Col span={6}>
              <Form.Item>
                <Input
                  type="number"
                  value={editedButton.workflowPlanId}
                  onChange={(e) => handleInputChange("workflowPlanId", Number(e.target.value))}
                  disabled={isDisabled}
                  data-testid="workflowPlanIdInput"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={8}>
              <label htmlFor="actionSelect">Action</label>
            </Col>
            <Col span={16}>
              <Form.Item>
                <Select
                  id="actionSelect"
                  value={editedButton.downloadToTemplate ? "Populate datacapture" : "Download to device"}
                  onChange={(value) => handleInputChange("downloadToTemplate", value === "Populate datacapture")}
                  disabled={isDisabled}
                  data-testid="actionInput"
                >
                  <Select.Option value="Download to device">Download to device</Select.Option>
                  <Select.Option value="Populate datacapture">Populate datacapture</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <h4>Metadata</h4>
              <Table
                dataSource={metadataRows}
                columns={metadataColumns}
                rowKey={(record) => record.id}
                pagination={false}
                size="small"
                locale={{ emptyText: <span className="no-data-span">No metadata</span> }}
              />
              <Button
                onClick={addMetadataRow}
                className="add-metadata-button"
                disabled={isDisabled}
                data-testid="addMetadataButton"
              >
                Add Metadata
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default ButtonEditModal;
